import React, { useState, useEffect } from 'react';
import { ResultDefinition } from '@exerai/vision-engine-web';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, FormControl, Select, MenuItem, Button, Tooltip, Box, Grid, Divider } from '@mui/material';
import { Control, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { Comparator } from '@/common/const';
import { Nullish } from '@/common/types';
import { DemographicsCategory } from '@/components/Scan/Outcomes/types/CriteriaCategory.enum';
import { FactorCategory } from '@/components/Scan/Outcomes/types/FactorCategory.enum';
import { OutcomeCategory } from '@/components/Scan/Outcomes/types/OutcomeCategory.enum';
import { outcomeUsesSidedResults } from '@/components/Scan/Outcomes/utils';
import { FormTextField } from '@/components/common/Form/Form';
import { FormFieldContainer } from '@/components/common/Form/styles';
import { AddIcon } from '@/components/common/Icons/AddIcon';
import { useOutcomeBuilderContext } from '../state/hooks/OutcomeBuilderContext';

// Helper function to convert UPPERCASE string to Pascal case
const toPascalCase = (str: string): string => {
    return str
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const OutcomeRisks = () => {
    const { control, errors, watch, assessment } = useOutcomeBuilderContext();

    const demographicsRequired = watch('demographicsRequired');
    const outcomeType = watch('type');
    const tremorsScale = watch('tremorsScale');
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'factors',
    });

    const resultDefinitionId = watch('resultDefinitionId');
    const leftResultDefinitionId = watch('leftResultDefinitionId');

    // Sided outcomes share one set of factors across both sides, so the left definition
    // supplies the metric type and units for the criteria fields. This assumes left and
    // right report the same type/units, which holds for every sided assessment today.
    const resultDefinition = outcomeUsesSidedResults(outcomeType, assessment)
        ? assessment.resultDefinitions.find((rd) => rd.id === leftResultDefinitionId)
        : assessment.resultDefinitions.find((rd) => rd.id === resultDefinitionId);

    if (demographicsRequired === 'false' && resultDefinition) {
        return (
            <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item md={12}>
                    <RiskFactorSection
                        control={control}
                        errors={errors}
                        fields={fields}
                        append={append}
                        remove={remove}
                        resultDefinition={resultDefinition}
                        outcomeType={outcomeType}
                        tremorsScale={tremorsScale}
                    />
                </Grid>
            </Grid>
        );
    }

    if (demographicsRequired === 'true' && resultDefinition && outcomeType === OutcomeCategory.FALL) {
        return (
            <Grid container direction="row" justifyContent="center" alignItems="center">
                <Grid item md={12}>
                    <NormativeFormSection
                        control={control}
                        errors={errors}
                        fields={fields}
                        append={append}
                        remove={remove}
                        resultDefinition={resultDefinition}
                    />
                </Grid>
            </Grid>
        );
    }

    return null;
};

interface RiskFactorSectionProps {
    control: Control<any>;
    errors: any;
    fields: any[];
    tremorsScale: Nullish<string>;
    resultDefinition: ResultDefinition;
    outcomeType: OutcomeCategory;
    append: (value: any) => void;
    remove: (index: number) => void;
}

// Component to automatically set short description based on category for non-FALL outcomes
const AutoShortDescription = ({
    control,
    factorIndex,
    outcomeType,
}: {
    control: Control<any>;
    factorIndex: number;
    outcomeType: OutcomeCategory;
}) => {
    const category = useWatch({
        control,
        name: `factors.${factorIndex}.category`,
    });

    const { setValue } = useOutcomeBuilderContext();

    useEffect(() => {
        if (outcomeType !== OutcomeCategory.FALL && category) {
            setValue(`factors.${factorIndex}.shortDescription`, toPascalCase(category));
        }
    }, [category, outcomeType, factorIndex, setValue]);

    return null;
};

const RiskFactorSection = ({
    control,
    errors,
    fields,
    append,
    remove,
    resultDefinition,
    outcomeType,
    tremorsScale,
}: RiskFactorSectionProps) => {
    const getAvailableCategories = () => {
        if (outcomeType === OutcomeCategory.FALL) {
            return [FactorCategory.LOW, FactorCategory.MEDIUM, FactorCategory.HIGH];
        }

        if (tremorsScale === 'UPDRS') {
            return [
                FactorCategory.NORMAL,
                FactorCategory.SLIGHT,
                FactorCategory.MILD,
                FactorCategory.MODERATE,
                FactorCategory.SEVERE,
            ];
        } else {
            // CRST
            return [
                FactorCategory.NONE,
                FactorCategory.SLIGHT,
                FactorCategory.MODERATE,
                FactorCategory.MARKED,
                FactorCategory.SEVERE,
            ];
        }
    };

    const availableCategories = getAvailableCategories();

    const getUsedCategories = () => {
        return fields.map((field) => field.category).filter(Boolean);
    };

    const getAvailableCategoriesForNewFactor = () => {
        const usedCategories = getUsedCategories();
        return availableCategories.filter((category) => !usedCategories.includes(category));
    };

    const getAvailableCategoriesForIndex = (currentIndex: number) => {
        const usedCategories = getUsedCategories();
        const currentCategory = fields[currentIndex]?.category;
        return availableCategories.filter(
            (category) => !usedCategories.includes(category) || category === currentCategory,
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'center',
            }}
        >
            {fields.map((field, index) => (
                <FormFieldContainer key={field.id} width={outcomeType === OutcomeCategory.TREMORS ? 200 : 230}>
                    {/* Auto-update short description for non-FALL outcomes */}
                    <AutoShortDescription control={control} factorIndex={index} outcomeType={outcomeType} />

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            marginBottom: '16px',
                        }}
                    >
                        {fields.length > 1 && outcomeType === OutcomeCategory.FALL && (
                            <Tooltip title="Delete Risk Factor" arrow>
                                <IconButton type="button" onClick={() => remove(index)} color="error" size="small">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                    <FormFieldContainer style={{ marginLeft: 0, marginBottom: 16 }}>
                        <h5>Risk Category</h5>
                        <Controller
                            name={`factors.${index}.category`}
                            control={control}
                            render={({ field }) => (
                                <FormControl variant="outlined" fullWidth>
                                    <Select
                                        {...field}
                                        displayEmpty
                                        MenuProps={{
                                            disablePortal: true,
                                        }}
                                        disabled={outcomeType === OutcomeCategory.TREMORS}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Category
                                        </MenuItem>
                                        {getAvailableCategoriesForIndex(index).map((category) => (
                                            <MenuItem key={category} value={category}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </FormFieldContainer>
                    <FormTextField
                        name={`factors.${index}.description`}
                        control={control}
                        errors={errors}
                        label="Description"
                        type="text"
                        rows={3}
                        style={{ marginLeft: 0 }}
                    />
                    {outcomeType === OutcomeCategory.FALL && (
                        <FormTextField
                            name={`factors.${index}.shortDescription`}
                            control={control}
                            errors={errors}
                            label="Short Description"
                            type="text"
                            style={{ marginLeft: 0 }}
                        />
                    )}
                    <CriteriaFieldArray
                        factorIndex={index}
                        control={control}
                        errors={errors}
                        outcomeType={outcomeType}
                        resultDefinition={resultDefinition}
                    />
                </FormFieldContainer>
            ))}
            {getAvailableCategoriesForNewFactor().length > 0 && outcomeType === OutcomeCategory.FALL && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 230,
                        height: '100%',
                        minHeight: '400px',
                    }}
                >
                    <Tooltip title="Add Risk Factor" arrow>
                        <Button
                            type="button"
                            onClick={() => {
                                const availableForNew = getAvailableCategoriesForNewFactor();
                                if (availableForNew.length > 0) {
                                    append({
                                        category: availableForNew[0],
                                        description: '',
                                        shortDescription: '',
                                        criteria: [
                                            {
                                                type: resultDefinition.type,
                                                units: resultDefinition.metric.units,
                                                conditions: [{ comparator: Comparator.gt, value: 0 }],
                                            },
                                        ],
                                    });
                                }
                            }}
                            variant="outlined"
                            size="large"
                        >
                            <AddIcon />
                        </Button>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

interface CriteriaFieldArrayProps {
    factorIndex: number;
    control: Control<any>;
    errors: any;
    outcomeType: OutcomeCategory;
    resultDefinition: ResultDefinition;
}

const CriteriaFieldArray = ({
    factorIndex,
    control,
    errors,
    outcomeType,
    resultDefinition,
}: CriteriaFieldArrayProps) => {
    const { fields, remove } = useFieldArray({
        control,
        name: `factors.${factorIndex}.criteria`,
    });

    return (
        <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <h6>Criteria</h6>
            </div>
            {fields.map((criteriaField, criteriaIndex) => {
                if (Object.values(DemographicsCategory).includes(criteriaField['type'])) {
                    return null;
                }
                return (
                    <div
                        key={criteriaField.id}
                        style={{
                            border: '1px solid #e0e0e0',
                            padding: '12px',
                            marginBottom: '8px',
                            borderRadius: '4px',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            {fields.length > 1 && outcomeType !== OutcomeCategory.FALL && (
                                <Tooltip title="Delete Criteria" arrow>
                                    <IconButton
                                        type="button"
                                        onClick={() => remove(criteriaIndex)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                        {/* Conditions */}
                        <ConditionsFieldArray
                            factorIndex={factorIndex}
                            criteriaIndex={criteriaIndex}
                            control={control}
                            errors={errors}
                            outcomeType={outcomeType}
                            resultDefinition={resultDefinition}
                        />
                    </div>
                );
            })}
        </div>
    );
};

interface ConditionsFieldArrayProps {
    factorIndex: number;
    criteriaIndex: number;
    control: Control<any>;
    errors: any;
    outcomeType: OutcomeCategory;
    resultDefinition: ResultDefinition;
}

const ConditionsFieldArray = ({
    factorIndex,
    criteriaIndex,
    control,
    errors,
    outcomeType,
    resultDefinition,
}: ConditionsFieldArrayProps) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: `factors.${factorIndex}.criteria.${criteriaIndex}.conditions`,
    });

    const getMaxConditions = () => {
        return 2;
    };

    const canAddCondition = fields.length < getMaxConditions();

    return (
        <div style={{ marginTop: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}></div>
            {fields.map((conditionField, conditionIndex) => (
                <div key={conditionField.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <Controller
                        name={`factors.${factorIndex}.criteria.${criteriaIndex}.conditions.${conditionIndex}.comparator`}
                        control={control}
                        render={({ field }) => (
                            <FormControl variant="outlined" size="small" style={{ margin: 0, width: 70 }}>
                                <Select
                                    {...field}
                                    MenuProps={{
                                        disablePortal: true,
                                    }}
                                >
                                    {Object.values(Comparator).map((comp) => (
                                        <MenuItem key={comp} value={comp}>
                                            {getComparatorDisplayName(comp)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Controller
                        name={`factors.${factorIndex}.criteria.${criteriaIndex}.conditions.${conditionIndex}.value`}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <FormTextField
                                {...field}
                                value={value}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const numValue = Number(e.target.value);
                                    onChange(isNaN(numValue) ? '' : numValue);
                                }}
                                errors={errors}
                                control={control}
                                label={resultDefinition.metric.units}
                                size="small"
                                style={{
                                    maxWidth: '60px',
                                    marginTop: 0,
                                    marginLeft: 8,
                                    marginRight: 8,
                                }}
                                margin="none"
                                type="number"
                                disableHelper
                            />
                        )}
                    />
                    {fields.length > 1 && (
                        <Tooltip title="Delete Condition" arrow>
                            <IconButton type="button" onClick={() => remove(conditionIndex)} color="error" size="small">
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            ))}
            {canAddCondition && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
                    <Tooltip title="Add Condition" arrow>
                        <Button
                            type="button"
                            onClick={() => append({ comparator: Comparator.gt, value: 0 })}
                            variant="outlined"
                            size="small"
                        >
                            <AddIcon />
                        </Button>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

interface NormativeFormSectionProps {
    control: Control<any>;
    errors: any;
    fields: any[];
    append: (value: any) => void;
    remove: (index: number) => void;
    resultDefinition: ResultDefinition;
}

// Custom hook to track viewport width
const useViewportWidth = () => {
    const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return width;
};

const NormativeFormSection = ({
    control,
    errors,
    fields,
    append,
    remove,
    resultDefinition,
}: NormativeFormSectionProps) => {
    const viewportWidth = useViewportWidth();

    const maleFactors = fields
        .filter((field, index) => {
            const genderValue = field.criteria?.[0]?.conditions?.[0]?.value;
            return genderValue === 'MALE';
        })
        .sort((a, b) => {
            const aMinAge = a.criteria?.[1]?.conditions?.[0]?.value || 0;
            const bMinAge = b.criteria?.[1]?.conditions?.[0]?.value || 0;
            return aMinAge - bMinAge;
        });

    const femaleFactors = fields
        .filter((field, index) => {
            const genderValue = field.criteria?.[0]?.conditions?.[0]?.value;
            return genderValue === 'FEMALE';
        })
        .sort((a, b) => {
            const aMinAge = a.criteria?.[1]?.conditions?.[0]?.value || 0;
            const bMinAge = b.criteria?.[1]?.conditions?.[0]?.value || 0;
            return aMinAge - bMinAge;
        });

    const getMaleFactorIndex = (maleIndex: number) => {
        const maleField = maleFactors[maleIndex];
        return fields.findIndex((field) => field.id === maleField.id);
    };

    const getFemaleFactorIndex = (femaleIndex: number) => {
        const femaleField = femaleFactors[femaleIndex];
        return fields.findIndex((field) => field.id === femaleField.id);
    };

    // Calculate if horizontal scroll would be needed based on current viewport
    const getJustifyContent = (factorCount: number) => {
        const cardWidth = 200;
        const gap = 16;
        const addButtonWidth = 200;
        const totalWidth = factorCount * cardWidth + (factorCount - 1) * gap + addButtonWidth + gap;
        const containerPadding = 64; // Account for container padding and margins
        const availableWidth = viewportWidth - containerPadding;

        return totalWidth > availableWidth ? 'flex-start' : 'center';
    };

    // Calculate the next minimum age for a new risk factor based on existing age ranges
    const getNextMinAge = (gender: 'MALE' | 'FEMALE') => {
        const genderFactors = gender === 'MALE' ? maleFactors : femaleFactors;
        if (genderFactors.length === 0) {
            return 60; // Default starting age
        }

        // Find the maximum age across all existing factors for this gender
        let maxAge = 0;
        genderFactors.forEach((factor) => {
            const ageConditions = factor.criteria?.[1]?.conditions;
            if (ageConditions && ageConditions.length > 1) {
                const maxAgeValue = ageConditions[1]?.value;
                if (typeof maxAgeValue === 'number' && maxAgeValue > maxAge) {
                    maxAge = maxAgeValue;
                }
            }
        });

        return maxAge > 0 ? maxAge + 1 : 60;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Male Row */}
            <Box>
                <h4 style={{ marginBottom: '8px', marginTop: '16px', textAlign: 'center' }}>Male</h4>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        justifyContent: getJustifyContent(maleFactors.length),
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#a1a1a1',
                        },
                    }}
                >
                    {maleFactors.map((field, maleIndex) => {
                        const actualIndex = getMaleFactorIndex(maleIndex);
                        return (
                            <Box key={field.id} sx={{ minWidth: 200, width: 200, flexShrink: 0 }}>
                                <FormFieldContainer width={200}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {maleFactors.length > 1 && (
                                            <Tooltip title="Delete Risk Factor" arrow>
                                                <IconButton
                                                    type="button"
                                                    onClick={() => remove(actualIndex)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>

                                    {/* Age and Result Criteria */}
                                    <NormativeCriteriaInput
                                        factorIndex={actualIndex}
                                        control={control}
                                        errors={errors}
                                        resultDefinition={resultDefinition}
                                        gender="MALE"
                                    />
                                </FormFieldContainer>
                            </Box>
                        );
                    })}

                    {/* Add Male Factor Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 200,
                            width: 200,
                            height: '100%',
                            minHeight: '220px',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title="Add Male Risk Factor" arrow>
                            <Button
                                type="button"
                                onClick={() => {
                                    const nextMinAge = getNextMinAge('MALE');
                                    append({
                                        category: FactorCategory.HIGH,
                                        description: null,
                                        shortDescription: 'High Risk',
                                        criteria: [
                                            {
                                                type: DemographicsCategory.SEX,
                                                units: null,
                                                conditions: [{ comparator: Comparator.e, value: 'MALE' }],
                                            },
                                            {
                                                type: DemographicsCategory.AGE,
                                                units: 'YEARS',
                                                conditions: [{ comparator: Comparator.gte, value: nextMinAge }],
                                            },
                                            {
                                                type: resultDefinition.type,
                                                units:
                                                    resultDefinition.metric.units === 'reps'
                                                        ? null
                                                        : resultDefinition.metric.units,
                                                conditions: [{ comparator: Comparator.gt, value: 0 }],
                                            },
                                        ],
                                    });
                                }}
                                variant="outlined"
                                size="large"
                            >
                                <AddIcon />
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
            <Divider />
            {/* Female Row */}
            <Box>
                <h4 style={{ marginBottom: '8px', marginTop: '16px', textAlign: 'center' }}>Female</h4>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '16px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        justifyContent: getJustifyContent(femaleFactors.length),
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#c1c1c1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#a1a1a1',
                        },
                    }}
                >
                    {femaleFactors.map((field, femaleIndex) => {
                        const actualIndex = getFemaleFactorIndex(femaleIndex);
                        return (
                            <Box key={field.id} sx={{ minWidth: 200, width: 200, flexShrink: 0 }}>
                                <FormFieldContainer width={200}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                        }}
                                    >
                                        {femaleFactors.length > 1 && (
                                            <Tooltip title="Delete Risk Factor" arrow>
                                                <IconButton
                                                    type="button"
                                                    onClick={() => remove(actualIndex)}
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>

                                    {/* Age and Result Criteria */}
                                    <NormativeCriteriaInput
                                        factorIndex={actualIndex}
                                        control={control}
                                        errors={errors}
                                        resultDefinition={resultDefinition}
                                        gender="FEMALE"
                                    />
                                </FormFieldContainer>
                            </Box>
                        );
                    })}

                    {/* Add Female Factor Button */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 200,
                            width: 200,
                            height: '100%',
                            minHeight: '300px',
                            flexShrink: 0,
                        }}
                    >
                        <Tooltip title="Add Female Risk Factor" arrow>
                            <Button
                                type="button"
                                onClick={() => {
                                    const nextMinAge = getNextMinAge('FEMALE');
                                    append({
                                        category: FactorCategory.HIGH,
                                        description: null,
                                        shortDescription: 'High Risk',
                                        criteria: [
                                            {
                                                type: DemographicsCategory.SEX,
                                                units: null,
                                                conditions: [{ comparator: Comparator.e, value: 'FEMALE' }],
                                            },
                                            {
                                                type: DemographicsCategory.AGE,
                                                units: 'YEARS',
                                                conditions: [{ comparator: Comparator.gte, value: nextMinAge }],
                                            },
                                            {
                                                type: resultDefinition.type,
                                                units:
                                                    resultDefinition.metric.units === 'reps'
                                                        ? null
                                                        : resultDefinition.metric.units,
                                                conditions: [{ comparator: Comparator.gt, value: 0 }],
                                            },
                                        ],
                                    });
                                }}
                                variant="outlined"
                                size="large"
                            >
                                <AddIcon />
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

interface NormativeCriteriaInputProps {
    factorIndex: number;
    control: Control<any>;
    errors: any;
    resultDefinition: ResultDefinition;
    gender: 'MALE' | 'FEMALE';
}

const NormativeCriteriaInput = ({
    factorIndex,
    control,
    errors,
    resultDefinition,
    gender,
}: NormativeCriteriaInputProps) => {
    return (
        <div>
            {/* Age Range Section */}
            <div>
                <h6 style={{ marginTop: '16px', marginBottom: '16px' }}>Age Range</h6>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Controller
                        name={`factors.${factorIndex}.criteria.1.conditions.0.value`}
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                            <FormTextField
                                {...field}
                                value={value}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const numValue = Number(e.target.value);
                                    onChange(isNaN(numValue) || numValue < 0 ? 0 : numValue);
                                }}
                                errors={errors}
                                control={control}
                                label="Min"
                                size="small"
                                style={{ width: '60px', marginLeft: 0, marginRight: 0 }}
                                margin="none"
                                type="number"
                                disableHelper
                                inputProps={{ min: 0 }}
                            />
                        )}
                    />
                    <span>to</span>
                    <Controller
                        name={`factors.${factorIndex}.criteria.1.conditions`}
                        control={control}
                        render={({ field: { onChange, value: conditions, ...field } }) => {
                            const maxValue = conditions?.[1]?.value || '';
                            return (
                                <FormTextField
                                    {...field}
                                    value={maxValue}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const numValue = Number(e.target.value);
                                        const newConditions = [conditions[0]]; // Always keep min condition

                                        if (!isNaN(numValue) && numValue > 0) {
                                            // Only add max condition if there's a valid value
                                            newConditions.push({
                                                comparator: Comparator.lte,
                                                value: numValue,
                                            });
                                        }

                                        onChange(newConditions);
                                    }}
                                    errors={errors}
                                    control={control}
                                    label="Max"
                                    size="small"
                                    style={{ width: '60px', marginLeft: 0, marginRight: 0 }}
                                    margin="none"
                                    type="number"
                                    disableHelper
                                    inputProps={{ min: 1 }}
                                />
                            );
                        }}
                    />
                </div>
            </div>

            {/* Result Criteria Section */}
            <div>
                <h6 style={{ marginTop: '16px', marginBottom: '16px' }}>Result Criteria</h6>
                <div
                    style={{
                        border: '1px solid #e0e0e0',
                        padding: '12px',
                        marginBottom: '8px',
                        borderRadius: '4px',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
                        <Controller
                            name={`factors.${factorIndex}.criteria.2.conditions.0.comparator`}
                            control={control}
                            render={({ field }) => (
                                <FormControl variant="outlined" size="small" style={{ margin: 0, width: 70 }}>
                                    <Select
                                        {...field}
                                        MenuProps={{
                                            disablePortal: true,
                                        }}
                                    >
                                        {Object.values(Comparator).map((comp) => (
                                            <MenuItem key={comp} value={comp}>
                                                {getComparatorDisplayName(comp)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name={`factors.${factorIndex}.criteria.2.conditions.0.value`}
                            control={control}
                            render={({ field: { onChange, value, ...field } }) => (
                                <FormTextField
                                    {...field}
                                    value={value}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const numValue = Number(e.target.value);
                                        onChange(isNaN(numValue) ? '' : numValue);
                                    }}
                                    errors={errors}
                                    control={control}
                                    label={resultDefinition.metric.units}
                                    size="small"
                                    style={{
                                        maxWidth: '80px',
                                        marginTop: 0,
                                        marginLeft: 8,
                                        marginRight: 8,
                                    }}
                                    margin="none"
                                    type="number"
                                    disableHelper
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const getComparatorDisplayName = (comparator: Comparator) => {
    switch (comparator) {
        case Comparator.gt:
            return '>';
        case Comparator.gte:
            return '>=';
        case Comparator.lt:
            return '<';
        case Comparator.lte:
            return '<=';
        case Comparator.e:
            return '=';
        default:
            return comparator;
    }
};
